<script setup lang="ts">
import { useInputStore } from '@/PC/store/inputStore'
import { getTimeZoneCity, COMMON_TIMEZONES } from '@/common/helpers/timezoneUtils'
import { storeToRefs } from 'pinia'
import { ref, computed, nextTick } from 'vue'

const inputStore = useInputStore()
const { secondaryTimeZone } = storeToRefs(inputStore)

const isOpen = ref(false)
const searchQuery = ref('')
const searchInput = ref<HTMLInputElement | null>(null)

const secondaryCity = computed(() => {
  if (!secondaryTimeZone.value) return null
  return getTimeZoneCity(secondaryTimeZone.value)
})

const filteredTimezones = computed(() => {
  if (!searchQuery.value) return COMMON_TIMEZONES
  const q = searchQuery.value.toLowerCase()
  return COMMON_TIMEZONES.filter(tz =>
    tz.city.toLowerCase().includes(q) ||
    tz.abbreviation.toLowerCase().includes(q) ||
    tz.value.toLowerCase().includes(q)
  )
})

const select = (tz: string) => {
  inputStore.setSecondaryTimeZone(tz)
  isOpen.value = false
  searchQuery.value = ''
}

const remove = () => {
  inputStore.setSecondaryTimeZone(null)
}

const toggle = () => {
  isOpen.value = !isOpen.value
  searchQuery.value = ''
  if (isOpen.value) {
    nextTick(() => searchInput.value?.focus())
  }
}

const closeDropdown = () => {
  isOpen.value = false
  searchQuery.value = ''
}
</script>

<template>
  <div class="tz-row" v-click-outside="closeDropdown">
    <template v-if="secondaryCity">
      <span class="tz-remove" @click="remove">
        <svg class="tz-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="7" stroke="currentColor" stroke-width="1.5"/>
          <path d="M7 7L13 13M13 7L7 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </span>
      <span class="tz-city" @click="toggle">{{ secondaryCity }}</span>
    </template>
    <template v-else>
      <span class="tz-add" @click="toggle">
        <svg class="tz-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="9" cy="10" r="7" stroke="currentColor" stroke-width="1.5"/>
          <path d="M9 6.5V10L11.5 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="15.5" cy="4.5" r="4" fill="white" stroke="none"/>
          <path d="M15.5 2.5V6.5M13.5 4.5H17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        <span>タイムゾーン</span>
      </span>
    </template>
    <div v-if="isOpen" class="tz-dropdown">
      <input
        ref="searchInput"
        class="tz-search"
        v-model="searchQuery"
        placeholder="相手のタイムゾーンを検索..."
        @click.stop
      />
      <ul class="tz-list">
        <li
          v-for="tz in filteredTimezones"
          :key="tz.value"
          class="tz-item"
          @click="select(tz.value)"
        >
          {{ tz.city }} ({{ tz.abbreviation }})
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.tz-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: var(--font-gray-2);
  height: 22px;
}
.tz-remove {
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-top: 2px;
}
.tz-remove:hover {
  opacity: 0.7;
}
.tz-city {
  cursor: pointer;
  white-space: nowrap;
}
.tz-city:hover {
  text-decoration: underline;
}
.tz-add {
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 2px;
  margin-top: 2px;
}
.tz-add:hover {
  text-decoration: underline;
}
.tz-icon {
  width: 18px;
  height: 18px;
}
.tz-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  width: 240px;
  background: #fff;
  border: 1px solid var(--border-gray-1, #ddd);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 100;
  overflow: hidden;
}
.tz-search {
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid var(--border-gray-1, #ddd);
  font-size: 13px;
  outline: none;
  box-sizing: border-box;
}
.tz-search::placeholder {
  color: var(--font-gray-3, #ccc);
}
.tz-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 200px;
  overflow-y: auto;
}
.tz-item {
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
}
.tz-item:hover {
  background: var(--bg-gray-1, #f0f0f0);
}
</style>
